// InspectionFormFields — the field body shared by the Create and Edit
// inspection drawers, in the design's order: name, description, form template,
// project, company structure (level + node), priority, tags, inspection window,
// location. Purely controlled — the parent drawers own the queries' data,
// validation flags and submit; this file also exports the draft shape and the
// `useInspectionFormData` hook so both drawers load reference data one way.
//
// Tags are select-only here (chips + library picker) — tag creation lives in
// the page-level TagsModal. The location link auto-fills coordinates when a
// Google Maps URL with an embedded lat,lng is pasted.

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Avatar,
  Chip,
  Dropdown,
  Field,
  Icon,
  Input,
  SelectDropdown,
  Switch,
  TagsPicker,
  Textarea,
} from '@/components/common';
import {
  useLazyGetInspectionScopeUsersQuery,
  type InspectionPriority,
} from '@/api/endpoints/inspectionApi';
import { useGetFormsSelectionQuery } from '@/api/endpoints/formsApi';
import { useGetProjectsSelectionQuery } from '@/api/endpoints/projectsApi';
import { useGetLevelsSelectionQuery } from '@/api/endpoints/levelsApi';
import { useGetNodesByLevelQuery } from '@/api/endpoints/structureApi';
import { useGetInspectionTagsQuery } from '@/api/endpoints/tagsApi';
import initialsOf from '@/utils/initials';

export interface InspectionDraft {
  /** null until picked — SelectDropdown's empty value. */
  form: string | null;
  project: string | null;
  title: string;
  description: string;
  priority: InspectionPriority;
  tagIds: string[];
  startDate: string;
  endDate: string;
  companyLevel: string | null;
  companyNode: string | null;
  location: string;
  coordinates: string;
  autoFill: boolean;
}

export const emptyInspectionDraft: InspectionDraft = {
  form: null,
  project: null,
  title: '',
  description: '',
  priority: 'medium',
  tagIds: [],
  startDate: '',
  endDate: '',
  companyLevel: null,
  companyNode: null,
  location: '',
  coordinates: '',
  autoFill: false,
};

/** End before start (both set) — YYYY-MM-DD strings compare lexicographically. */
export const isWindowInvalid = (draft: InspectionDraft): boolean =>
  !!draft.startDate && !!draft.endDate && draft.endDate < draft.startDate;

const PRIORITIES: { value: InspectionPriority; icon: string }[] = [
  { value: 'low', icon: 'chevron-down' },
  { value: 'medium', icon: 'equal' },
  { value: 'high', icon: 'chevron-up' },
];

// lat,lng embedded in a Google Maps URL — "/@21.54,39.17,15z" or "?q=21.54,39.17".
const coordsFromMapsLink = (link: string): string | null => {
  const match =
    link.match(/@(-?\d{1,3}\.\d+),(-?\d{1,3}\.\d+)/) ??
    link.match(/[?&]q=(-?\d{1,3}\.?\d*),\s*(-?\d{1,3}\.?\d*)/);
  return match ? `${match[1]}, ${match[2]}` : null;
};

/** Reference data for the pickers — fetched only while a drawer is open;
 *  nodes additionally wait for a chosen level. */
export const useInspectionFormData = (open: boolean, levelId: string | null) => {
  const { data: forms = [] } = useGetFormsSelectionQuery(undefined, { skip: !open });
  const { data: projects = [] } = useGetProjectsSelectionQuery(undefined, { skip: !open });
  const { data: levels = [] } = useGetLevelsSelectionQuery(undefined, { skip: !open });
  const { data: inspectionTags = [] } = useGetInspectionTagsQuery(undefined, { skip: !open });
  const { data: nodes = [] } = useGetNodesByLevelQuery(levelId, {
    skip: !open || !levelId,
    refetchOnMountOrArgChange: true,
  });
  return { forms, projects, levels, inspectionTags, nodes };
};

export type InspectionFormData = ReturnType<typeof useInspectionFormData>;

interface InspectionFormFieldsProps {
  draft: InspectionDraft;
  patch: (changes: Partial<InspectionDraft>) => void;
  data: InspectionFormData;
  /** Structural fields (form/project/level/node) disabled — non-draft edit. */
  locked?: boolean;
  /** Show errors on every create-required field (name/form/project/level). */
  showRequiredErrors: boolean;
  /** Show the name error alone (draft saves require only the name). */
  showTitleError: boolean;
  /** Edit mode: the assigned-to box becomes a "View assignees" dropdown that
   *  fetches the automatic scope's users (POST /inspections/scope-users). */
  viewAssignees?: boolean;
}

const InspectionFormFields = ({
  draft,
  patch,
  data,
  locked = false,
  showRequiredErrors,
  showTitleError,
  viewAssignees = false,
}: InspectionFormFieldsProps) => {
  const { t } = useTranslation();
  const { forms, projects, levels, inspectionTags, nodes } = data;

  const formMissing = !draft.form;
  const projectMissing = !draft.project;
  const levelMissing = !draft.companyLevel;
  const windowInvalid = isWindowInvalid(draft);
  // Locked selects can't be fixed by the user, so they never flag errors.
  const structureErrors = showRequiredErrors && !locked;

  const formOptions = useMemo(
    () => forms.map((f) => ({ value: f.id, label: f.name, icon: f.icon })),
    [forms],
  );
  const projectOptions = useMemo(
    () => projects.map((p) => ({ value: p.id, label: p.name, icon: p.icon })),
    [projects],
  );
  const levelOptions = useMemo(
    () => levels.map((l) => ({ value: l.id, label: l.name, icon: 'layers' })),
    [levels],
  );
  const nodeOptions = useMemo(
    () => [
      {
        value: null,
        label: draft.companyLevel
          ? t('inspection_node_placeholder')
          : t('inspection_node_pick_level'),
        icon: 'layers',
      },
      ...nodes.map((n) => ({ value: n.id, label: n.name, icon: 'git-branch' })),
    ],
    [nodes, draft.companyLevel, t],
  );

  const tagById = useMemo(
    () => new Map(inspectionTags.map((tag) => [tag.id, tag.title])),
    [inspectionTags],
  );

  const removeTag = (id: string) => patch({ tagIds: draft.tagIds.filter((x) => x !== id) });

  // "View assignees" (edit mode) — fetch the scope's users when the panel
  // opens; the lazy query caches per scope, so reopening is instant.
  const [assigneesOpen, setAssigneesOpen] = useState(false);
  const [fetchScopeUsers, scopeUsers] = useLazyGetInspectionScopeUsersQuery();
  const canViewAssignees = viewAssignees && !!draft.project && !!draft.companyLevel;
  const toggleAssignees = () => {
    // `draft.project` narrows here; the trigger only renders once it is set
    // (canViewAssignees), so this never skips a fetch the user could see.
    if (!assigneesOpen && draft.project && draft.companyLevel)
      fetchScopeUsers(
        {
          project: draft.project,
          companyLevel: draft.companyLevel,
          companyNodes: draft.companyNode ? [draft.companyNode] : [],
        },
        true,
      );
    setAssigneesOpen((o) => !o);
  };
  const levelName = levels.find((l) => l.id === draft.companyLevel)?.name ?? '';

  // Pasting a maps link with an embedded lat,lng fills the coordinates too.
  const changeLocation = (value: string) => {
    const coords = coordsFromMapsLink(value);
    patch(coords ? { location: value, coordinates: coords } : { location: value });
  };

  return (
    <div className='inspection-create'>
      <Field
        label={t('inspection_field_title')}
        required
        error={showTitleError ? t('inspection_title_required') : undefined}
      >
        <Input
          iconStart='clipboard-list'
          value={draft.title}
          placeholder={t('inspection_field_title_placeholder')}
          error={showTitleError}
          onChange={(e) => patch({ title: e.target.value })}
        />
      </Field>

      <Field label={t('inspection_field_description')}>
        <Textarea
          rows={3}
          placeholder={t('inspection_field_description_placeholder')}
          value={draft.description}
          onChange={(e) => patch({ description: e.target.value })}
        />
      </Field>

      <Field
        label={t('inspection_field_form')}
        required
        hint={t('inspection_field_form_hint')}
        error={structureErrors && formMissing ? t('inspection_form_required') : undefined}
      >
        <SelectDropdown
          iconStart='file-text'
          size='lg'
          portal
          flip
          value={draft.form}
          placeholder={t('inspection_field_form_placeholder')}
          options={formOptions}
          error={structureErrors && formMissing}
          disabled={locked}
          onChange={(next) => patch({ form: next })}
        />
      </Field>

      <Field
        label={t('inspection_field_project')}
        required
        hint={t('inspection_field_project_hint')}
        error={structureErrors && projectMissing ? t('inspection_project_required') : undefined}
      >
        <SelectDropdown
          iconStart='folder-kanban'
          size='lg'
          portal
          flip
          value={draft.project}
          placeholder={t('inspection_field_project_placeholder')}
          options={projectOptions}
          error={structureErrors && projectMissing}
          disabled={locked}
          onChange={(next) => patch({ project: next })}
        />
      </Field>

      <div className='inspection-create__group'>
        <h3 className='inspection-create__group-title'>
          {t('inspection_group_structure')}
          <span className='inspection-create__group-required'> *</span>
        </h3>
        <Field
          label={t('inspection_field_level')}
          required
          hint={t('inspection_level_hint')}
          error={structureErrors && levelMissing ? t('inspection_level_required') : undefined}
        >
          <SelectDropdown
            iconStart='layers'
            size='lg'
            portal
            flip
            value={draft.companyLevel}
            placeholder={t('inspection_level_placeholder')}
            options={levelOptions}
            error={structureErrors && levelMissing}
            disabled={locked}
            // Changing the level clears the node (it belongs to the old level).
            onChange={(next) => patch({ companyLevel: next, companyNode: null })}
          />
        </Field>
        <Field label={t('inspection_field_node')} optional hint={t('inspection_node_hint')}>
          <SelectDropdown
            iconStart='map-pin'
            size='lg'
            portal
            flip
            value={draft.companyNode}
            options={nodeOptions}
            disabled={locked || !draft.companyLevel}
            onChange={(next) => patch({ companyNode: next })}
          />
        </Field>
      </div>

      <Field label={t('inspection_field_priority')}>
        <div
          className='inspection-create__priority'
          role='group'
          aria-label={t('inspection_field_priority')}
        >
          {PRIORITIES.map((p) => {
            const selected = draft.priority === p.value;
            return (
              <button
                key={p.value}
                type='button'
                aria-pressed={selected}
                className={`inspection-create__priority-btn inspection-create__priority-btn--${p.value}${
                  selected ? ' is-selected' : ''
                }`}
                onClick={() => patch({ priority: p.value })}
              >
                <Icon name={p.icon} size={15} />
                {t(`inspection_priority_${p.value}`)}
              </button>
            );
          })}
        </div>
      </Field>

      {/* Display-only per the design: assignment is inherited from the project
          and company structure ("Automatic") — nothing is submitted from here.
          Edit mode swaps the placeholder for a "View assignees" dropdown. */}
      <Field
        label={t('inspection_field_assigned')}
        labelTag={
          <>
            <Icon name='lock' size={13} aria-hidden='true' />
            {t('inspection_assigned_automatic')}
          </>
        }
        hint={
          canViewAssignees && !draft.companyNode
            ? t('inspection_assigned_scope_hint', { level: levelName })
            : t('inspection_assigned_hint')
        }
      >
        {canViewAssignees ? (
          <Dropdown
            open={assigneesOpen}
            onClose={() => setAssigneesOpen(false)}
            align='start'
            className='inspection-create__assignees'
            trigger={
              <button
                type='button'
                className='inspection-create__assignees-trigger'
                aria-expanded={assigneesOpen}
                onClick={toggleAssignees}
              >
                <Icon name='users-round' size={18} className='inspection-create__assignees-icon' />
                <span className='inspection-create__assignees-action'>
                  {t('inspection_assigned_view')}
                  <Icon name={assigneesOpen ? 'chevron-up' : 'chevron-down'} size={15} />
                </span>
              </button>
            }
          >
            <div className='inspection-create__assignees-panel'>
              {scopeUsers.isFetching ? (
                <span className='inspection-create__assignees-empty'>{t('text_loading')}</span>
              ) : (scopeUsers.data ?? []).length === 0 ? (
                <span className='inspection-create__assignees-empty'>
                  {t('inspection_assigned_empty')}
                </span>
              ) : (
                (scopeUsers.data ?? []).map((person) => {
                  const fullName = `${person.firstName} ${person.lastName}`.trim();
                  return (
                    <div key={person.id} className='inspection-create__assignees-row'>
                      <Avatar initials={initialsOf(fullName)} size={28} />
                      <span className='inspection-create__assignees-name'>{fullName}</span>
                    </div>
                  );
                })
              )}
            </div>
          </Dropdown>
        ) : (
          <Input
            className='inspection-create__assigned'
            iconStart='users'
            disabled
            value=''
            placeholder={t('inspection_assigned_placeholder')}
            onChange={() => {}}
          />
        )}
      </Field>

      <Field label={t('inspection_field_tags')} optional>
        <div className='inspection-create__tags'>
          {draft.tagIds.length > 0 && (
            <div className='inspection-create__tag-chips'>
              {draft.tagIds.map((id) => (
                <Chip key={id} variant='brand' iconStart='tag'>
                  {tagById.get(id) ?? id}
                  <button
                    type='button'
                    className='inspection-create__tag-remove'
                    aria-label={t('unit_tag_remove', { tag: tagById.get(id) ?? id })}
                    onClick={() => removeTag(id)}
                  >
                    <Icon name='x' size={12} />
                  </button>
                </Chip>
              ))}
            </div>
          )}

          {inspectionTags.length === 0 ? (
            <span className='inspection-create__tags-empty'>{t('inspection_tags_none')}</span>
          ) : (
            <TagsPicker
              tags={inspectionTags}
              selectedIds={draft.tagIds}
              onChange={(ids) => patch({ tagIds: ids })}
              placeholder={t('inspection_tags_placeholder')}
            />
          )}
        </div>
      </Field>

      <Field
        label={t('inspection_field_window')}
        optional
        hint={t('inspection_window_hint')}
        error={windowInvalid ? t('inspection_dates_invalid') : undefined}
      >
        <div className='inspection-create__dates'>
          <Input
            type='date'
            iconStart='calendar'
            value={draft.startDate}
            error={windowInvalid}
            onChange={(e) => patch({ startDate: e.target.value })}
          />
          <Input
            type='date'
            iconStart='calendar-check'
            value={draft.endDate}
            error={windowInvalid}
            onChange={(e) => patch({ endDate: e.target.value })}
          />
        </div>
      </Field>

      <Field label={t('inspection_field_location')} optional hint={t('inspection_location_hint')}>
        <div className='inspection-create__location'>
          <Input
            iconStart='link'
            placeholder={t('inspection_location_placeholder')}
            value={draft.location}
            onChange={(e) => changeLocation(e.target.value)}
          />
          <Input
            iconStart='map-pin'
            placeholder={t('inspection_coordinates_placeholder')}
            value={draft.coordinates}
            onChange={(e) => patch({ coordinates: e.target.value })}
          />
        </div>
      </Field>

      {/* Auto-fill — only meaningful (and only shown) once a level + node are picked. */}
      {draft.companyLevel && draft.companyNode && (
        <div className='inspection-create__autofill'>
          <div className='inspection-create__autofill-text'>
            <span className='inspection-create__autofill-label'>
              {t('inspection_autofill_label')}
            </span>
            <span className='inspection-create__autofill-hint'>
              {t(draft.autoFill ? 'inspection_autofill_hint_on' : 'inspection_autofill_hint')}
            </span>
          </div>
          <Switch
            aria-label={t('inspection_autofill_label')}
            checked={draft.autoFill}
            onChange={(e) => patch({ autoFill: e.target.checked })}
          />
        </div>
      )}
    </div>
  );
};

export default InspectionFormFields;
